import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import authService from "../../services/auth.service";
import { LoginCredentials } from "../../types";
import "./Login.scss";

type LoginMode = "user" | "admin";

const parseMode = (value: string | null): LoginMode =>
  value === "user" ? "user" : "admin";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = parseMode(searchParams.get("mode"));
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingSession, setCheckingSession] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [clientId, setClientId] = useState<string>("");
  const [tokenId, setTokenId] = useState<string>("");
  const [authMethod, setAuthMethod] = useState<string>("IDPW");

  const finalizeAuthorization = useCallback(
    async (currentTokenId: string, currentMode: LoginMode): Promise<void> => {
      const response = await authService.completeAuthorization(currentTokenId, currentMode);
      window.location.href = response.redirectUrl;
    },
    [],
  );

  const checkExistingSession = useCallback(
    async (
      currentMode: LoginMode,
      currentRedirectUrl: string,
      currentTokenId: string,
    ): Promise<void> => {
      try {
        setCheckingSession(true);
        const response =
          currentMode === "user"
            ? await authService.validateUser()
            : await authService.validateAdmin();

        if (response.valid && response.user) {
          authService.saveUser(response.user);

          if (currentTokenId) {
            await finalizeAuthorization(currentTokenId, currentMode);
          } else if (currentRedirectUrl) {
            window.location.href = currentRedirectUrl;
          } else {
            navigate(`/${currentMode}/dashboard`);
          }
        }
      } catch (error) {
        authService.clearUser();
      } finally {
        setCheckingSession(false);
      }
    },
    [navigate, finalizeAuthorization],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isLogout = urlParams.get("logout") === "true";

    if (isLogout) {
      authService.clearAllData();
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const redirect = urlParams.get("redirect");
    if (redirect) {
      setRedirectUrl(redirect);
    }

    const nextClientId = urlParams.get("client_id") || "";
    const nextTokenId = urlParams.get("token_id") || "";
    const nextMethod = urlParams.get("method") || "IDPW";
    const initialMode = parseMode(urlParams.get("mode"));

    setClientId(nextClientId);
    setTokenId(nextTokenId);
    setAuthMethod(nextMethod);

    checkExistingSession(initialMode, redirect || "", nextTokenId);
    // checkExistingSession is stable via useCallback; effect still runs once per mount.
  }, [checkExistingSession]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response =
        mode === "user"
          ? await authService.loginUser(credentials, redirectUrl)
          : await authService.loginAdmin(credentials, redirectUrl);

      if (response.success && response.user) {
        authService.saveUser(response.user);

        if (tokenId) {
          await finalizeAuthorization(tokenId, mode);
        } else if (response.redirectUrl || redirectUrl) {
          window.location.href = response.redirectUrl || redirectUrl;
        } else {
          navigate(`/${mode}/dashboard`);
        }
      }
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
        "Login failed. Please check your credentials.",
      );
      authService.clearUser();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  if (checkingSession) {
    return <Loader message="Checking authentication..." />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-brand__mark" aria-hidden="true">SAT</span>
          <div className="login-brand__text">
            <span className="login-brand__wordmark">
              SATashkent<span className="login-brand__sso">SSO</span>
            </span>
            <span className="login-brand__tagline">You Will Never Walk Alone</span>
          </div>
        </div>

        {(clientId || redirectUrl) && (
          <div className="login-header">
            {clientId && (
              <div className="redirect-info">
                <span>Signing in to:</span>
                <strong>{clientId}</strong>
                <small>{authMethod}</small>
              </div>
            )}
            {redirectUrl && (
              <div className="redirect-info">
                <span>Redirecting to:</span>
                <strong>{new URL(redirectUrl).hostname}</strong>
              </div>
            )}
          </div>
        )}

        <div
          className={`role-badge role-badge--${mode}`}
          role="status"
          aria-label={mode === "user" ? "Student portal" : "Admin portal"}
        >
          <span className="role-badge__icon" aria-hidden="true">
            {mode === "user" ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10 12 5 2 10l10 5 10-5Z" />
                <path d="M6 12v5c0 1 2.7 3 6 3s6-2 6-3v-5" />
                <path d="M22 10v6" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            )}
          </span>
          {mode === "user" ? "Student Portal" : "Admin Portal"}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">
              {mode === "user" ? "Student Login or Email" : "Admin Login or Email"}
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder={
                mode === "user"
                  ? "Enter student login or email"
                  : "Enter admin login or email"
              }
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="login-button">
            {loading
              ? "Signing in..."
              : mode === "user"
                ? "Sign In as Student"
                : "Sign In as Admin"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
